import React, { useCallback, useEffect, useState } from 'react';
import { Container, Spinner, Alert, Button } from 'react-bootstrap';
import { FaLinkedin, FaExternalLinkAlt, FaSyncAlt, FaRegThumbsUp, FaRegCommentDots, FaEye, FaUsers, FaGlobe } from 'react-icons/fa';
import { useOutletContext } from 'react-router-dom';
import { fetchCompanyPosts, fetchLinkedInStats, syncLinkedInPosts } from '../services/linkedinService';
import { getImageUrl } from '../constants';
import { RichText } from '../utils/textRender';
import bynetLogo from '../assets/bynet-logo.png';

const PAGE_SIZE = 5;
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

// Client-side cache (persists across page navigations within the session) so
// returning to the page shows content instantly and refreshes quietly in the background.
let pageCache = { posts: [], total: 0, hasMore: false, loaded: false };
let statsCacheClient = null;

const formatNumber = (n) => {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('he-IL').format(n);
};

const formatRelative = (timestamp) => {
  if (!timestamp) return '';
  const diff = Date.now() - Number(timestamp);
  const day = 24 * 60 * 60 * 1000;
  if (diff < day) return 'היום';
  if (diff < 2 * day) return 'אתמול';
  if (diff < 7 * day) return `לפני ${Math.floor(diff / day)} ימים`;
  if (diff < 30 * day) return `לפני ${Math.floor(diff / (7 * day))} שבועות`;
  try {
    return new Date(Number(timestamp)).toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
};

const getPostImage = (post) => {
  const raw = post?.mediaThumbnail || post?.article?.thumbnail || null;
  return raw ? getImageUrl(raw) : null;
};

const formatLastSync = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
};

const StatCard = ({ icon, label, value, accent }) => (
  <div
    style={{
      flex: '1 1 140px',
      background: '#fff',
      borderRadius: 12,
      padding: '14px 16px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      display: 'flex',
      alignItems: 'center',
      gap: 12
    }}
  >
    <div
      style={{
        width: 42,
        height: 42,
        borderRadius: 10,
        background: accent || '#eef3f8',
        color: '#0a66c2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        flexShrink: 0
      }}
    >
      {icon}
    </div>
    <div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, lineHeight: 1.1, color: '#1a202c' }}>{value}</div>
      <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{label}</div>
    </div>
  </div>
);

const PostCard = ({ post }) => {
  const imageUrl = getPostImage(post);
  const postUrl = post?.permalink;
  const isNew = post?.publishedAt && Number(post.publishedAt) >= Date.now() - SEVEN_DAYS_MS;

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        marginBottom: 20,
        border: '1px solid #e6e9ec'
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px 6px' }}>
        <img
          src={bynetLogo}
          alt="Bynet Data Centers"
          style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'contain', border: '1px solid #004182', background: '#0a66c2', padding: 6 }}
        />
        <div style={{ flexGrow: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: '#1a202c', display: 'flex', alignItems: 'center', gap: 6 }}>
            Bynet Data Centers
            {isNew && (
              <span style={{ background: '#bf2e1a', color: '#fff', fontSize: '0.65rem', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
                NEW
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{formatRelative(post.publishedAt)}</div>
        </div>
        <FaLinkedin size={22} color="#0a66c2" />
      </div>

      {/* Commentary */}
      <div style={{ padding: '4px 16px 12px', color: '#1a202c', fontSize: '0.95rem', lineHeight: 1.6 }}>
        <RichText text={post.commentary} />
      </div>

      {/* Media image */}
      {imageUrl && (
        <a href={postUrl || '#'} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
          <img
            src={imageUrl}
            alt={post?.article?.title || 'תמונה מפוסט לינקדאין'}
            style={{ width: '100%', maxHeight: 460, objectFit: 'cover', display: 'block', borderTop: '1px solid #eef2f5', borderBottom: '1px solid #eef2f5' }}
            onError={(e) => { if (e.target) e.target.style.display = 'none'; }}
          />
        </a>
      )}

      {/* Article preview */}
      {post.article?.title && (
        <a
          href={post.article.url || postUrl || '#'}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'block', margin: '12px 16px', padding: 14, background: '#f3f6f8', borderRadius: 10, textDecoration: 'none', border: '1px solid #e6e9ec' }}
        >
          <div style={{ fontWeight: 700, color: '#1a202c' }}>{post.article.title}</div>
          {post.article.description && (
            <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 4 }}>{post.article.description}</div>
          )}
          {post.article.url && (
            <div style={{ fontSize: '0.78rem', color: '#0a66c2', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <FaGlobe size={11} /> {(() => { try { return new URL(post.article.url).hostname.replace('www.', ''); } catch { return 'קישור'; } })()}
            </div>
          )}
        </a>
      )}

      {/* Footer */}
      {postUrl && (
        <div style={{ borderTop: '1px solid #eef2f5', padding: '8px 16px' }}>
          <a
            href={postUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#0a66c2', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            צפה בפוסט בלינקדאין <FaExternalLinkAlt size={13} />
          </a>
        </div>
      )}
    </div>
  );
};

const CompanyPostsPage = () => {
  const { user } = useOutletContext() || {};
  const isAdmin = user?.role === 'admin';

  const [posts, setPosts] = useState(pageCache.posts);
  const [total, setTotal] = useState(pageCache.total);
  const [hasMore, setHasMore] = useState(pageCache.hasMore);
  const [loading, setLoading] = useState(!pageCache.loaded);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [stats, setStats] = useState(statsCacheClient);

  const loadInitial = useCallback(async () => {
    try {
      // Only show the full-screen spinner on the very first load; otherwise refresh quietly.
      if (!pageCache.loaded) setLoading(true);
      setError('');
      const data = await fetchCompanyPosts({ limit: PAGE_SIZE, offset: 0 });
      setPosts(data.items);
      setTotal(data.total);
      setHasMore(data.hasMore);
      pageCache = { posts: data.items, total: data.total, hasMore: data.hasMore, loaded: true };
    } catch (err) {
      console.error('Failed to load LinkedIn posts:', err);
      if (!pageCache.loaded) {
        setError('לא הצלחנו לטעון את הפוסטים. ודאו שהשרת פועל ונסו שוב.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  useEffect(() => {
    fetchLinkedInStats()
      .then((data) => { setStats(data); statsCacheClient = data; })
      .catch((err) => console.error('Failed to load LinkedIn stats:', err));
  }, []);

  const handleLoadMore = async () => {
    try {
      setLoadingMore(true);
      const data = await fetchCompanyPosts({ limit: PAGE_SIZE, offset: posts.length });
      setPosts((prev) => [...prev, ...data.items]);
      setTotal(data.total);
      setHasMore(data.hasMore);
    } catch (err) {
      console.error('Failed to load more posts:', err);
      setError('לא הצלחנו לטעון פוסטים נוספים.');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncMessage('');
      const result = await syncLinkedInPosts();
      const summary = result?.summary || {};
      setSyncMessage(`רענון הושלם: ${summary.fetched || 0} נמשכו, ${summary.created || 0} חדשים, ${summary.updated || 0} עודכנו.`);
      await loadInitial();
      fetchLinkedInStats().then(setStats).catch(() => {});
    } catch (err) {
      console.error('Sync failed:', err);
      setSyncMessage(`הרענון נכשל: ${err?.message || 'שגיאה לא ידועה'}`);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Container className="py-4" dir="rtl" style={{ maxWidth: 680 }}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="d-flex align-items-center gap-2 mb-1" style={{ color: '#0a66c2' }}>
            <FaLinkedin size={30} />
            פוסטים מלינקדאין
          </h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            עדכונים ישירות מעמוד החברה – בינת דאטה סנטרס
          </p>
          {stats?.lastSyncAt && (
            <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>
              עדכון אוטומטי אחרון: {formatLastSync(stats.lastSyncAt)}
            </div>
          )}
        </div>
        {isAdmin && (
          <Button variant="primary" onClick={handleSync} disabled={syncing} className="d-inline-flex align-items-center gap-2">
            <FaSyncAlt className={syncing ? 'fa-spin' : ''} />
            {syncing ? 'מרענן...' : 'רענן עכשיו'}
          </Button>
        )}
      </div>

      {/* Stats band */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 22 }}>
        <StatCard icon={<FaUsers />} label="עוקבים" value={formatNumber(stats?.followers)} accent="#e3f0ff" />
        <StatCard icon={<FaEye />} label="חשיפות" value={formatNumber(stats?.impressions)} accent="#eafaf1" />
        <StatCard icon={<FaRegThumbsUp />} label="לייקים" value={formatNumber(stats?.likes)} accent="#fdeeec" />
        <StatCard icon={<FaRegCommentDots />} label="תגובות" value={formatNumber(stats?.comments)} accent="#f3eefd" />
      </div>

      {syncMessage && <Alert variant="info" className="py-2">{syncMessage}</Alert>}

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">טוען פוסטים מלינקדאין...</p>
        </div>
      )}

      {!loading && error && <Alert variant="danger" className="text-center">{error}</Alert>}

      {!loading && !error && posts.length === 0 && (
        <Alert variant="info" className="text-center">
          לא נמצאו פוסטים להצגה. {isAdmin ? 'לחצו "רענן עכשיו" לסנכרון ראשוני.' : 'הפוסטים יופיעו לאחר סנכרון.'}
        </Alert>
      )}

      {!loading && !error && posts.map((post) => <PostCard key={post.id} post={post} />)}

      {!loading && hasMore && (
        <div className="text-center mt-2 mb-4">
          <Button variant="outline-primary" onClick={handleLoadMore} disabled={loadingMore} style={{ borderRadius: 24, padding: '8px 28px' }}>
            {loadingMore ? 'טוען...' : `טען עוד פוסטים (${total - posts.length} נותרו)`}
          </Button>
        </div>
      )}
    </Container>
  );
};

export default CompanyPostsPage;
